// spendRollup — group trip scans by drawer (location) and tile, sum
// prices, and emit a structure the CheckoutScreen renders as the
// per-drawer spend summary. Pure function; the caller passes scans +
// receipt-line match map.
//
// Inputs:
//   scans      — array of trip_scans-shape objects (from useShopTrip)
//   priceByScanId — Map<scanId, { lineIndex, priceCents, tier, lineText }>
//                   produced by matchReceiptLines.
//
// Output:
//   {
//     totalCents,
//     byLocation: [{ location, totalCents, tiles: [{ tileId, totalCents,
//                    items: [{ scanId, name, qty, lineCents, tier,
//                              lineText }] }] }],
//     unmatched: [{ scanId, name, qty }]   // scans with no paired line
//   }
//
// Sort: locations alphabetical (fridge/freezer/pantry render in
// consistent order); tiles within a location sorted by total descending
// so the biggest spend reads first.

export function rollupSpend(scans, priceByScanId) {
  const total = { cents: 0 };
  const locations = new Map();   // location → { totalCents, tiles: Map<tileId, ...> }
  const unmatched = [];

  for (const scan of (scans || [])) {
    const matched = priceByScanId?.get?.(scan.id) || null;
    if (!matched) {
      unmatched.push({
        scanId: scan.id,
        name: scan.displayName || scan.productName || null,
        qty: scan.qty || 1,
      });
      continue;
    }
    const lineCents = typeof matched.priceCents === "number" ? matched.priceCents : null;
    total.cents += lineCents || 0;

    const locKey = scan.location || "Unsorted";
    if (!locations.has(locKey)) {
      locations.set(locKey, { totalCents: 0, tiles: new Map() });
    }
    const loc = locations.get(locKey);
    loc.totalCents += lineCents || 0;

    const tileKey = scan.tileId || "—";
    if (!loc.tiles.has(tileKey)) {
      loc.tiles.set(tileKey, { totalCents: 0, items: [] });
    }
    const tile = loc.tiles.get(tileKey);
    tile.totalCents += lineCents || 0;
    tile.items.push({
      scanId: scan.id,
      name: scan.displayName || scan.productName || null,
      qty: scan.qty || 1,
      lineCents,
      tier: matched.tier || null,
      lineText: matched.lineText || null,
    });
  }

  const byLocation = [...locations.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([location, locData]) => ({
      location,
      totalCents: locData.totalCents,
      tiles: [...locData.tiles.entries()]
        .sort((a, b) => b[1].totalCents - a[1].totalCents)
        .map(([tileId, tileData]) => ({
          tileId,
          totalCents: tileData.totalCents,
          items: tileData.items,
        })),
    }));

  return {
    totalCents: total.cents,
    byLocation,
    unmatched,
  };
}

export function formatCents(cents) {
  if (cents == null || !Number.isFinite(Number(cents))) return "—";
  const n = Number(cents);
  return `$${(n / 100).toFixed(2)}`;
}
